import {
  ChartBarIcon,
  UserCircleIcon,
  PhoneArrowUpRightIcon,
  ArrowPathRoundedSquareIcon,
  DevicePhoneMobileIcon,
  ClipboardDocumentCheckIcon,
  CloudArrowUpIcon,
  RocketLaunchIcon,
  SparklesIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  HandRaisedIcon,
  ArrowTrendingUpIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

// Animation variants
export const fadeInUp = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

export const features = [
  {
    icon: DevicePhoneMobileIcon,
    title: 'AI Voice Agents',
    description:
      'Configure voice, personality, and goals to run natural outbound conversations at scale.',
    color: 'from-blue-500 to-cyan-400',
    borderColor: 'border-blue-400',
  },
  {
    icon: ArrowPathRoundedSquareIcon,
    title: 'Campaign Management',
    description:
      'Launch, pause, stop, and retry campaigns with clear run states and operational visibility.',
    color: 'from-indigo-500 to-violet-500',
    borderColor: 'border-indigo-400',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'Post-Call Intelligence',
    description:
      'Extract lead temperature, disposition, objection themes, goal completion, and next-best follow-up actions after every call.',
    color: 'from-amber-500 to-pink-500',
    borderColor: 'border-amber-400',
  },
  {
    icon: PhoneArrowUpRightIcon,
    title: 'Inbound Return Calls',
    description:
      'Allow leads to call back and reconnect with your AI agent using inbound call flows.',
    color: 'from-emerald-500 to-cyan-400',
    borderColor: 'border-emerald-400',
  },
  {
    icon: ChartBarIcon,
    title: 'Analytics Dashboard',
    description:
      'Track calls, outcomes, duration trends, and credit usage with actionable performance insights.',
    color: 'from-purple-500 to-pink-500',
    borderColor: 'border-purple-400',
  },
];

export const howItWorks = [
  {
    icon: UserCircleIcon,
    title: 'Create Your AI Agent',
    description: 'Set voice, personality, audience, and call goals for consistent outreach.',
  },
  {
    icon: CloudArrowUpIcon,
    title: 'Upload Your Leads',
    description: 'Import CSV/Excel lists or add contacts manually to start quickly.',
  },
  {
    icon: RocketLaunchIcon,
    title: 'Launch Your Campaign',
    description: 'Run automated calls with controls to start, pause, stop, and retry.',
  },
  {
    icon: SparklesIcon,
    title: 'Review Intelligence',
    description:
      'Review rich call intelligence including summaries, objections, intent, lead temperature, and recommended next actions.',
  },
];

export const valueOutcomes = [
  {
    icon: ClockIcon,
    title: '24/7 Prospecting',
    description:
      'Your AI caller keeps running while you are in inspections, meetings, or offline, so leads do not cool down.',
  },
  {
    icon: CurrencyDollarIcon,
    title: 'Lower Cost Per Conversation',
    description:
      'Scale outbound volume at a fraction of traditional caller cost while keeping quality and consistency high.',
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'Consistent Messaging',
    description:
      'Every call follows your brand voice, offer positioning, and objection-handling framework without drift.',
  },
  {
    icon: HandRaisedIcon,
    title: 'Focus on Warm Leads',
    description:
      'The system qualifies and prioritizes prospects so your team spends time where buying intent is strongest.',
  },
  {
    icon: ArrowTrendingUpIcon,
    title: 'Scale Without Hiring',
    description:
      'Increase daily call volume dramatically without expanding headcount, rosters, or management overhead.',
  },
  {
    icon: PaperAirplaneIcon,
    title: 'Immediate Follow-up',
    description:
      'Trigger fast follow-up workflows so prospects hear back while the conversation is still top-of-mind.',
  },
];

export const faqs = [
  {
    question: 'How natural does the AI sound?',
    answer:
      'Agent Noda uses human-like conversational voice models and configurable prompts so calls sound natural and context-aware. You can test this firsthand using the free credits included when you sign up.',
  },
  {
    question: 'How does the AI handle complex objections?',
    answer:
      'The assistant handles complex objections using your configured call goals and conversation guardrails to keep calls constructive and move leads toward clear next steps.',
  },
  {
    question: 'How do credits work?',
    answer:
      'Each plan includes monthly credits for calling activity. 1 credit is 1 minute of calling time (rounded to 60 seconds). You can monitor usage in-app and buy add-on packs when needed.',
  },
  {
    question: 'Can leads call me back?',
    answer:
      'Yes. Inbound return calls are supported on eligible plans so leads can reconnect with your AI assistant after outbound outreach.',
  },
  {
    question: 'What integrations do you support?',
    answer:
      'You can import leads via CSV/Excel today. Enterprise tier supports custom integrations and API-based workflows for deeper system connectivity.',
  },
  {
    question: 'Can I try before I buy?',
    answer:
      'Yes. Every new account starts with 20 free credits and no credit card is required. You can evaluate real call flows first, then upgrade when you are ready to scale.',
  },
];
